/*
 * Time-zone arithmetic with Intl (no extra library): the calendar shows times in the user's profile time zone and
 * forms edit wall-clock times in the event's zone, whatever the browser's own zone is. Zone ids are current IANA names
 * (@/shared/lib/timeZones: Intl's "Europe/Kiev" is "Europe/Kyiv" here).
 */
import { COMMON_TIME_ZONES, intlTimeZone, supportedTimeZones } from '@/shared/lib/timeZones';

export { browserTimeZone } from '@/shared/lib/timeZones';

/** Wall-clock parts in a time zone (month 1–12). */
export interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: intlTimeZone(timeZone),
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    formatters.set(timeZone, f);
  }
  return f;
}

/** Whether the runtime knows the IANA zone. */
export function isValidTimeZone(timeZone: string | null | undefined): timeZone is string {
  if (!timeZone) return false;
  try {
    formatter(timeZone);
    return true;
  } catch {
    return false;
  }
}

/** Wall-clock time of an instant in a zone. */
export function toWallClock(instant: Date | number, timeZone: string): WallClock {
  const parts = formatter(timeZone).formatToParts(typeof instant === 'number' ? instant : instant.getTime());
  const get = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour') % 24,
    minute: get('minute'),
    second: get('second'),
  };
}

const asUtc = (w: WallClock) => Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);

/** Offset of the zone at an instant, in minutes east of UTC (Kyiv: 120 in winter, 180 in summer). */
export function offsetMinutes(instant: Date | number, timeZone: string): number {
  const ms = typeof instant === 'number' ? instant : instant.getTime();
  const rounded = Math.floor(ms / 1000) * 1000;
  return Math.round((asUtc(toWallClock(rounded, timeZone)) - rounded) / 60_000);
}

/**
 * The instant of a wall-clock time in a zone. Times skipped by a DST change move forward by the gap; ambiguous
 * times take the first (earlier) instant — like the backend.
 */
export function fromWallClock(wall: WallClock, timeZone: string): Date {
  const guess = asUtc(wall);
  const matches = (candidate: number) => asUtc(toWallClock(candidate, timeZone)) === guess;
  const first = guess - offsetMinutes(guess, timeZone) * 60_000;
  const second = guess - offsetMinutes(first, timeZone) * 60_000;
  if (first === second) {
    // Clocks turned back: the same wall clock an hour earlier is the first of the two.
    return new Date(matches(first - 3_600_000) ? first - 3_600_000 : first);
  }
  // DST transition: prefer the candidate whose wall clock matches; otherwise (a gap) the later one.
  if (matches(Math.min(first, second))) return new Date(Math.min(first, second));
  if (matches(Math.max(first, second))) return new Date(Math.max(first, second));
  return new Date(Math.max(first, second));
}

/**
 * A `Date` whose *local* fields equal the wall clock of `instant` in `timeZone` — what date pickers (which work
 * in the browser's zone) should show for that zone.
 */
export function toPickerDate(instant: Date | string, timeZone: string): Date {
  const w = toWallClock(new Date(instant), timeZone);
  return new Date(w.year, w.month - 1, w.day, w.hour, w.minute, 0, 0);
}

/** The instant meant by a picker date (local fields = wall clock in `timeZone`). */
export function fromPickerDate(picker: Date, timeZone: string): Date {
  return fromWallClock(
    {
      year: picker.getFullYear(),
      month: picker.getMonth() + 1,
      day: picker.getDate(),
      hour: picker.getHours(),
      minute: picker.getMinutes(),
      second: 0,
    },
    timeZone,
  );
}

const pad = (n: number) => String(n).padStart(2, '0');

/** `YYYY-MM-DD` of a wall clock / picker date. */
export function isoDate(value: Date | WallClock): string {
  if (value instanceof Date)
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
  return `${value.year}-${pad(value.month)}-${pad(value.day)}`;
}

/** Parses `YYYY-MM-DD` into a local-midnight Date (picker date); null when malformed. */
export function parseIsoDate(value: string | null | undefined): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/u.exec(value ?? '');
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

/** All-day values travel as the UTC date part: `2026-11-04T00:00:00Z` → `2026-11-04`. */
export function utcDate(value: string | Date): string {
  const d = new Date(value);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** A picker date's day as the UTC midnight the API expects for all-day events. */
export function allDayToApi(picker: Date): string {
  return `${isoDate(picker)}T00:00:00Z`;
}

/** "GMT+3" / "GMT−5:30" label for a zone at an instant. */
export function gmtLabel(timeZone: string, at: Date = new Date()): string {
  const minutes = offsetMinutes(at, timeZone);
  if (minutes === 0) return 'GMT';
  const sign = minutes > 0 ? '+' : '−';
  const abs = Math.abs(minutes);
  const hours = Math.floor(abs / 60);
  const rest = abs % 60;
  return `GMT${sign}${hours}${rest ? `:${pad(rest)}` : ''}`;
}

/** Every zone the runtime supports under its current name: the common ones (Kyiv first), then the rest. */
export function allTimeZones(): string[] {
  const rest = supportedTimeZones().filter((z) => !COMMON_TIME_ZONES.includes(z));
  return [...COMMON_TIME_ZONES.filter((z) => isValidTimeZone(z)), ...rest];
}

/** Minutes → `Date` math without DST surprises for durations. */
export const addMinutes = (date: Date, minutes: number) => new Date(date.getTime() + minutes * 60_000);

/** The next half hour from now, as a picker date (new events start there). */
export function nextHalfHour(now: Date = new Date()): Date {
  const d = new Date(now);
  d.setSeconds(0, 0);
  d.setMinutes(d.getMinutes() < 30 ? 30 : 60);
  return d;
}
